import React from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { 
  Zap, 
  Sparkles, 
  Layers, 
  Shirt, 
  Grid, 
  FolderOpen, 
  Rocket 
} from 'lucide-react';
import './UsagePlan.css';

const UsagePlan = () => {
  const { profile, loading } = useOutletContext();

  if (loading) return <div className="loading-state">Loading dashboard...</div>;
  if (!profile) return null;

  // --- 1. DATA & LOGIC ---
  const tier = (profile.subscription_tier || 'starter').toLowerCase();
  const firstName = profile.full_name ? profile.full_name.split(' ')[0] : 'Creator';
  
  // Limits
  const starterMockupLimit = 3;
  const starterAiLimit = 10; 
  const liteBase = 150;

  // Usage Data
  const dailyUsed = profile.daily_usage_count || 0;
  const aiUsed = profile.ai_tools_daily_usage || 0;
  const creditsBalance = profile.credits_balance || 0;

  // Calculations
  const starterMockupRemaining = Math.max(0, starterMockupLimit - dailyUsed);
  const starterMockupPercent = (starterMockupRemaining / starterMockupLimit) * 100;
  
  const starterAiRemaining = Math.max(0, starterAiLimit - aiUsed);
  const starterAiPercent = (starterAiRemaining / starterAiLimit) * 100;

  const litePercent = Math.min(100, (creditsBalance / liteBase) * 100);

  // Time-based Greeting (Uses User's Local Browser Time)
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 5) return 'Good night';
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="dashboard-container fade-in">
      
      {/* --- 1. WELCOME BANNER --- */}
      <div className="welcome-banner">
        <div>
          <h1>{getGreeting()}, {firstName}!</h1>
          <p>Here is what’s happening with your account today.</p>
        </div>
        <div className="plan-badge-wrapper">
            <span className={`plan-pill ${tier}`}>{tier} Plan</span>
        </div>
      </div>

      {/* --- 2. QUICK STATS GRID --- */}
      <div className="stats-grid">
        
        {/* CARD A: MOCKUP CREDITS */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon-wrapper"><Layers size={20} /></span>
            <span className="stat-title">Mockup Credits</span>
          </div>
          
          <div className="stat-body">
            {tier === 'starter' ? (
                <>
                    <div className="stat-value">{starterMockupRemaining} <span className="stat-total">/ {starterMockupLimit}</span></div>
                    <div className="progress-container">
                        <div className="progress-bar" style={{ width: `${starterMockupPercent}%` }}></div>
                    </div>
                    <p className="stat-desc">Resets daily. Use them to generate mockups.</p>
                </>
            ) : tier === 'lite' ? (
                <>
                    <div className="stat-value">{creditsBalance}</div>
                    <div className="progress-container">
                        <div className="progress-bar" style={{ width: `${litePercent}%` }}></div>
                    </div>
                    <p className="stat-desc">Monthly credits remaining.</p>
                </>
            ) : (
                <>
                    <div className="stat-value">∞</div>
                    <p className="stat-desc" style={{marginTop: '0.5rem'}}>Unlimited generation enabled.</p>
                </>
            )}
          </div>
        </div>

        {/* CARD B: AI TOOLS */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon-wrapper"><Sparkles size={20} /></span>
            <span className="stat-title">AI Tools</span>
          </div>
          
          <div className="stat-body">
            {tier === 'starter' ? (
                <>
                    <div className="stat-value">{starterAiRemaining} <span className="stat-total">/ {starterAiLimit}</span></div>
                    <div className="progress-container">
                        <div className="progress-bar blue" style={{ width: `${starterAiPercent}%` }}></div>
                    </div>
                    <p className="stat-desc">Resets daily. Use different tools for edit the images.</p>
                </>
            ) : (
                <>
                    <div className="stat-value">∞</div>
                    <div className="progress-container">
                        <div className="progress-bar blue" style={{ width: '100%' }}></div>
                    </div>
                    <p className="stat-desc">Unlimited access to all AI tools.</p>
                </>
            )}
          </div>
        </div>

        {/* CARD C: STATUS / UPGRADE */}
        <div className="stat-card action-card">
          <div className="stat-header">
            <span className="stat-icon-wrapper"><Rocket size={20} /></span>
            <span className="stat-title">Current Status</span>
          </div>
          <div className="stat-body">
             {tier === 'agency' || tier === 'pro' ? (
                 <div className="active-status">
                    <h3>All Systems Go</h3>
                    <p>You are on the highest tier. Enjoy creating!</p>
                 </div>
             ) : (
                 <div className="upgrade-status">
                    <p>Unlock <strong>Unlimited</strong> generations and priority support.</p>
                    <Link to="/pricing" className="btn-upgrade">
                       <Zap size={16} style={{marginRight:'8px'}}/> Upgrade Plan
                    </Link>
                 </div>
             )}
          </div>
        </div>

      </div>

      {/* --- 3. RECENT ACTIVITY PREVIEW --- */}
      <div className="section-header">
        <h2>Quick Actions</h2>
      </div>
      
      <div className="quick-actions-grid">
         <Link to="/app/apparel" className="action-tile">
            <span className="tile-icon"><Shirt size={28} /></span>
            <span>New Mockup</span>
         </Link>
         
         <Link to="/tool" className="action-tile">
            <span className="tile-icon"><Grid size={28} /></span>
            <span>Explore Tools</span>
         </Link>
         
         <Link to="/account/gallery" className="action-tile">
            <span className="tile-icon"><FolderOpen size={28} /></span>
            <span>View Gallery</span>
         </Link>
      </div>

    </div>
  );
};

export default UsagePlan;